import { useEffect, useRef, useState } from 'react'
import * as BABYLON from '@babylonjs/core'
import { buildSpacesRoom, FurnitureItemInfo, HallPhoto } from './buildSpacesRoom'
import { FurnitureKind, RoomPresetId } from './roomPresets'

export interface UseSpacesRoomSceneResult {
  items: FurnitureItemInfo[]
  selectedId: string | null
  setPreset: (preset: RoomPresetId) => void
  addFurniture: (kind: FurnitureKind) => void
  rotateSelected: () => void
  removeSelected: () => void
}

/** Owns buildSpacesRoom's lifecycle for one scene, mirrors its item/selection callbacks into React state. */
export function useSpacesRoomScene(
  scene: BABYLON.Scene | null,
  canvasRef: React.RefObject<HTMLCanvasElement>,
  initialPreset: RoomPresetId,
  photos: HallPhoto[],
): UseSpacesRoomSceneResult {
  const [items, setItems] = useState<FurnitureItemInfo[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const apiRef = useRef<ReturnType<typeof buildSpacesRoom> | null>(null)

  useEffect(() => {
    if (!scene || !canvasRef.current) return
    const api = buildSpacesRoom(scene, canvasRef.current, initialPreset, photos, {
      onItemsChange: setItems,
      onSelectChange: setSelectedId,
    })
    apiRef.current = api
    return () => {
      apiRef.current = null
      api.dispose()
    }
    // Only the scene/canvas identity re-triggers a full rebuild — preset switches go through
    // the returned `setPreset` API instead so they don't tear down and recreate the hook itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, photos])

  return {
    items,
    selectedId,
    setPreset: (preset) => apiRef.current?.setPreset(preset),
    addFurniture: (kind) => apiRef.current?.addFurniture(kind),
    rotateSelected: () => apiRef.current?.rotateSelected(),
    removeSelected: () => apiRef.current?.removeSelected(),
  }
}
